import { FormEvent } from "react";
import { Input, DateInput, RichTextEditor, Select, LoadingButton, TagCombobox } from "../../../../components/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

export interface DiaryEntryFormData {
  date: string;
  description: string;
  tag: string;
  status: string;
  weather: string;
}

interface DiaryEntryFormProps {
  formData: DiaryEntryFormData;
  onFieldChange: (field: keyof DiaryEntryFormData, value: string) => void;
  onSubmit: (e: FormEvent) => void;
  loading: boolean;
  error: Error | undefined;
  onCancel: () => void;
  /** When set, form shows "EDITAR ENTRADA" instead of "NOVA ENTRADA" */
  title?: string;
  /** When "naked", no card wrapper (e.g. for use inside a modal). Default "default". */
  variant?: "default" | "naked";
}

const DEFAULT_FORM_TITLE = "NOVA ENTRADA";

export default function DiaryEntryForm({
  formData,
  onFieldChange,
  onSubmit,
  loading,
  error,
  onCancel,
  title = DEFAULT_FORM_TITLE,
  variant = "default",
}: DiaryEntryFormProps) {
  const content = (
    <>
      <h3 className="text-lg font-light text-primary-950 dark:text-white mb-6">
        {title}
      </h3>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}
      <form onSubmit={onSubmit} className="space-y-6">
        <DateInput
          id="diary-date"
          label="Data"
          value={formData.date}
          onChange={(e) => onFieldChange("date", e.target.value)}
          required
        />
        <RichTextEditor
          id="diary-description"
          label="Descrição"
          value={formData.description}
          onChange={(value) => onFieldChange("description", value)}
          placeholder="Descreva o que aconteceu..."
          minHeight="280px"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <TagCombobox
            id="diary-tag"
            label="Etiqueta"
            value={formData.tag}
            onChange={(value) => onFieldChange("tag", value)}
            placeholder="ex: Cozinha, Sala de estar"
          />
          <Select
            id="diary-status"
            label="Status"
            value={formData.status}
            onChange={(e) => onFieldChange("status", e.target.value)}
            required
          >
            <option value="normal">Normal</option>
            <option value="delayed">Atrasado</option>
            <option value="ahead_of_schedule">Adiantado</option>
            <option value="issue">Problema</option>
            <option value="milestone">Marco</option>
          </Select>
          <Input
            id="diary-weather"
            label="Clima"
            type="text"
            value={formData.weather}
            onChange={(e) => onFieldChange("weather", e.target.value)}
            placeholder="ex: Ensolarado"
          />
        </div>
        <div className="flex gap-4">
          <LoadingButton
            type="submit"
            loading={loading}
            loadingText="SALVANDO..."
            className="bg-primary-950 dark:bg-white text-white dark:text-primary-950 px-8 py-3 text-sm uppercase tracking-widest font-light hover:bg-primary-800 dark:hover:bg-primary-100 transition-colors duration-300 cursor-pointer disabled:cursor-not-allowed"
          >
            SALVAR
          </LoadingButton>
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={onCancel}
          >
            CANCELAR
          </Button>
        </div>
      </form>
    </>
  );

  if (variant === "naked") return content;
  return (
    <Card>
      <CardContent className="pt-8">{content}</CardContent>
    </Card>
  );
}
